import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
  useLocation,
  useHistory,
} from 'react-router-dom';
import { hot } from 'react-hot-loader';
import { utils, NotFound, RouteWithSubRoutes, SessionProvider, FlagProvider, useFeatures, useSession } from '@cordata/buffet';
import Helmet from 'react-helmet';
import { toast, ToastContainer } from 'react-toastify';
import { Container } from 'reactstrap';

import { Navbar, AppLayout } from '~/components/common';
import { routes, authorizedInstance as axios } from '~/utils';

import ApiProvider from '~/utils/api/context';
import SliceProvider from '~/utils/slice/context';
import DuplicateProvider from '~/utils/duplicate/context';

const Paths = () => {
  const { hasAppKickstarter } = useFeatures();
  const { isAuthed } = useSession();

  if (window.location.pathname === '/' && isAuthed) return (<Redirect to="/admin" />);
  if (window.location.pathname === '/' && !isAuthed) return (<Redirect to="/login" />);

  return (
    <>
      <Helmet
        titleTemplate={`%s - ${hasAppKickstarter ? 'Kickstarter' : 'Admin'} - Cordata`}
      />

      <Route
        path="/"
        render={props => (
          <Navbar {...props} routes={routes.filter(r => r.main)} />
        )}
      />

      <DuplicateProvider>
        <Container fluid>
          <Switch>
            {routes.map(r =>
              <RouteWithSubRoutes key={r.path} {...r} />)}

            <Route path="*"><NotFound isRoute /></Route>
          </Switch>
        </Container>
      </DuplicateProvider>
    </>
  );
};

const OnRamp = () => {
  const location = useLocation();
  const history = useHistory();

  const userInPrivateRoutes = () =>
    location.pathname !== '/' &&
    !location.pathname.includes('login');

  useEffect(() => {
    const routeAttempted = localStorage.getItem('route-attempted');
    const ra = JSON.parse(routeAttempted);

    if (routeAttempted && location.pathname !== ra.pathname && userInPrivateRoutes()) {
      toast.info('Redirecting to previous page ...');

      localStorage.removeItem('route-attempted');

      history.replace(ra.pathname, ra.state);
    }
  }, []);

  return (
    <SessionProvider userInPrivateRoutes={userInPrivateRoutes}>
      <utils.sessionTimeout.SessionTimeoutProvider>
        <FlagProvider>
          <ApiProvider>
            <SliceProvider>
              <Paths />
            </SliceProvider>
          </ApiProvider>
        </FlagProvider>
      </utils.sessionTimeout.SessionTimeoutProvider>
    </SessionProvider>
  );
};

const App = () => {
  useEffect(() => {
    if (!localStorage.getItem('destinations')) {
      axios
        .get('/destinations')
        .then(r => r.data.data)
        .then((r) => {
          localStorage.setItem('destinations', JSON.stringify(r));
        });
    }
  }, []);

  return (
    <AppLayout>
      <Router
        getUserConfirmation={(message, callback) => {
          const allowTransition = window.confirm(message);

          callback(allowTransition);
        }}
      >
        <OnRamp />

        <ToastContainer
          position="bottom-center"
          autoClose={2500}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </Router>
    </AppLayout>
  );
};

export default hot(module)(App);
